// "धातु + प्रत्यय". सामान्य: one card per standard form, root + (marker) + ending → (what changes) → the form, e.g.
// हस + मि → अ → आ → हसामि. विकल्प: one card per person and number with the notes' formula (हस + (अ/आ/ए) + मि) and
// every form worked out (① हस + (अ → आ) + मि = हसामि). When every person and number share the forms (भूत,
// क्रियातिपत्ति), they are shown once.

import type { FC, ReactNode } from 'react'
import type { Lang, Num, Person } from '@/prakrit/lib/grammar/types'
import type { PratyayaForm, PratyayaTable } from '@/prakrit/lib/grammar/pratyaya'
import type { KalMode } from '@/prakrit/components/grammar/kal/kal-tabs'

import { useState } from 'react'
import { ArrowDown, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NUMBERS, PERSONS } from '@/prakrit/lib/grammar/types'
import { NUMBER_LABEL, PERSON_LABEL } from '@/prakrit/lib/grammar/labels'
import { hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'
import { PANEL } from '@/prakrit/components/grammar/dhatu/dhatu-ui'

import {
    STEM_CHANGE,
    endingLabel,
    formulaOf,
    sameInEveryCell
} from '@/prakrit/lib/grammar/pratyaya'

interface FormationCardsProps {
    root: string
    table: PratyayaTable
    mode: KalMode
}

interface CellItem {
    key: string
    label: Lang
    forms: PratyayaForm[]
}

const ALL_CELLS: Lang = { hi: 'सभी पुरुष और वचन', en: 'every person and number' }

const labelOf = (p: Person, n: Num): Lang => ({ hi: `${PERSON_LABEL[p].hi} · ${NUMBER_LABEL[n].hi}`, en: `${PERSON_LABEL[p].en}, ${NUMBER_LABEL[n].en}` })

const cellsOf = (table: PratyayaTable, pick: (p: Person, n: Num) => PratyayaForm[]): CellItem[] => {
    if (sameInEveryCell(table)) return [{ key: 'all', label: ALL_CELLS, forms: pick('uttam', 'eka') }]

    return PERSONS.flatMap(p => NUMBERS.map(n => ({ key: `${p}.${n}`, label: labelOf(p, n), forms: pick(p, n) })))
}

const Part: FC<{ children: ReactNode, tone?: 'root' | 'ending' }> = ({ children, tone = 'root' }): ReactNode => (
    <span
        lang='pra'
        className={cn(
            'inline-flex min-h-10 items-center rounded-xl px-3 font-prakrit text-lg font-semibold leading-none',
            tone === 'root' ? 'bg-secondary text-foreground' : 'bg-saffron/15 text-saffron-strong'
        )}
    >
        {children}
    </span>
)

const Label: FC<{ label: Lang }> = ({ label }): ReactNode => (
    <p className='flex flex-wrap items-baseline gap-x-1.5 text-xs text-muted-foreground'>
        <span lang='hi' className='hindi font-medium'>{label.hi}</span>
        <span aria-hidden='true'>·</span>
        <span lang='en'>{label.en}</span>
    </p>
)

/** सामान्य: one card per standard form. */
const SamanyaCards: FC<{ root: string, table: PratyayaTable }> = ({ root, table }): ReactNode => {
    const items = cellsOf(table, (p, n) => table[p][n].samanya).flatMap(c => c.forms.map(form => ({ key: `${c.key}|${form.form}`, label: c.label, form })))

    return (
        <ul className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
            {items.map((item, i) => {
                const { form } = item

                return (
                    <li
                        key={item.key}
                        style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }}
                        className={cn(PANEL, 'flex flex-col items-center gap-2.5 px-5 py-5 text-center motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-500 motion-safe:fill-mode-both')}
                    >
                        <Label label={item.label} />
                        <p className='flex flex-wrap items-center justify-center gap-2'>
                            <Part>{root}</Part>
                            {form.marker && (
                                <>
                                    <span aria-hidden='true' className='text-muted-foreground'>+</span>
                                    <Part tone='ending'>{form.marker}</Part>
                                </>
                            )}
                            <span aria-hidden='true' className='text-muted-foreground'>+</span>
                            <Part tone='ending'>{endingLabel({ ending: form.ending })}</Part>
                            <span className='sr-only'>{` = ${form.form}`}</span>
                        </p>
                        <ArrowDown className='size-4 text-muted-foreground' aria-hidden='true' />
                        {form.change && (
                            <p className='text-xs text-muted-foreground'>
                                <span lang='hi' className='hindi'>{STEM_CHANGE[form.change].hi}</span>
                                <span aria-hidden='true'> · </span>
                                <span lang='en'>{STEM_CHANGE[form.change].en}</span>
                            </p>
                        )}
                        <p lang='pra' className='pra pra-xl text-prakrit'>{form.form}</p>
                        {form.rule && <p className='adults-only text-[0.6875rem] text-muted-foreground'>{form.rule}</p>}
                    </li>
                )
            })}
        </ul>
    )
}

const VOWEL_STEP: Partial<Record<string, string>> = { aa: 'अ → आ', e: 'अ → ए', i: 'अ → इ', avi: 'अ → इ', ae: 'अ विकरण → ए', de: 'दा → दे' }

/** "हस + (अ → आ) + मि = हसामि" */
const Derivation: FC<{ root: string, form: PratyayaForm }> = ({ root, form }): ReactNode => {
    const step = form.change ? VOWEL_STEP[form.change] : undefined

    return (
        <span lang='pra' className='font-prakrit text-[0.9375rem]'>
            <span>{root}</span>
            {step && <span className='text-muted-foreground'> + ({step})</span>}
            {form.marker && <span> + {form.marker}</span>}
            <span> + </span>
            <span className='font-semibold text-saffron-strong'>{endingLabel({ ending: form.ending })}</span>
            <span className='text-muted-foreground'> = </span>
            <span className='font-semibold text-prakrit'>{form.form}</span>
        </span>
    )
}

const SHOWN = 8

const VikalpCard: FC<{ root: string, item: CellItem }> = ({ root, item }): ReactNode => {
    const [open, setOpen] = useState(false)
    const formulas = formulaOf(root, item.forms)
    const list = open ? item.forms : item.forms.slice(0, SHOWN)
    const hidden = item.forms.length - list.length

    return (
        <li className={cn(PANEL, 'flex flex-col gap-3 p-5')}>
            <div className='flex items-start justify-between gap-3'>
                <Label label={item.label} />
                <span className='shrink-0 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium'>
                    <span lang='hi' className='hindi'>{hiDigits(item.forms.length)} रूप</span>
                    <span className='sr-only'> · {item.forms.length} forms</span>
                </span>
            </div>
            <div className='rounded-xl bg-surface-muted/80 px-3.5 py-2.5'>
                <p className='text-xs font-semibold text-muted-foreground'>
                    <span lang='hi' className='hindi'>सूत्र</span>
                    <span aria-hidden='true'> · </span>
                    <span lang='en'>Formula</span>
                </p>
                <ul className='mt-1 flex flex-col gap-0.5'>
                    {formulas.map(f => <li key={f} lang='pra' className='font-prakrit text-base font-semibold'>{f}</li>)}
                </ul>
            </div>
            <ol className='flex flex-col gap-1.5'>
                {list.map((form, i) => (
                    <li key={form.form} className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5'>
                        <span aria-hidden='true' className='w-6 shrink-0 text-right text-xs text-muted-foreground'>{hiDigits(i + 1)}.</span>
                        <Derivation root={root} form={form} />
                        {form.status === 'core' && (
                            <span className='rounded-full bg-terracotta px-2 py-0.5 text-[0.6875rem] font-semibold text-background'>
                                <span lang='hi' className='hindi'>सामान्य</span>
                            </span>
                        )}
                    </li>
                ))}
            </ol>
            {item.forms.length > SHOWN && (
                <button
                    type='button'
                    aria-expanded={open}
                    onClick={() => setOpen(v => !v)}
                    className='inline-flex min-h-tap items-center gap-1.5 self-start text-sm font-semibold text-saffron-strong hover:underline'
                >
                    <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} aria-hidden='true' />
                    <span lang='hi' className='hindi'>{open ? 'कम दिखाएँ' : `और ${hiDigits(hidden)} रूप`}</span>
                    <span aria-hidden='true'>·</span>
                    <span lang='en'>{open ? 'Show fewer' : `${hidden} more`}</span>
                </button>
            )}
        </li>
    )
}

export const FormationCards: FC<FormationCardsProps> = ({ root, table, mode }): ReactNode => {
    if (mode === 'samanya') return <SamanyaCards root={root} table={table} />

    const items = cellsOf(table, (p, n) => table[p][n].all)

    return (
        <ul className='grid gap-4 md:grid-cols-2'>
            {items.map(item => <VikalpCard key={item.key} root={root} item={item} />)}
        </ul>
    )
}
